import * as React from 'react';
import { FunctionComponent, useEffect, useState } from 'react';
import DayPicker,  { DateUtils } from 'react-day-picker';
import 'react-day-picker/lib/style.css';
import './FullCalendar.scss';
import {
  eachDayOfInterval,
  eachWeekendOfMonth,
  endOfYear,
  format,
  getDaysInMonth,
  getYear, isEqual, isSameMonth, isToday,
  isWeekend,
  startOfYear
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import { SelectedDays, SpecialDay } from '../../types';
import {Tooltip, Popover, Divider } from '@iba-team/portal-react-components/dist';
import { observer } from 'mobx-react';
import { useCheckAccess, useDeviceSizeThreshold } from '../../utils/hooks';
import { chooseEventTypeModalCall, deleteEventModalCall, editEventModalCall } from './CalendarPage';
import { BREAKPOINT_LG } from '../../consts';
import {ReactComponent as StarIcon} from '../../assets/icons/star.svg';
import { ReactComponent as WorkingIcon } from '../../assets/icons/reports/working-day.svg';
import {ReactComponent as CloseIcon} from '@iba-team/portal-react-components/dist/svg/outline/x.svg';
import { ReactComponent as DeleteIcon } from '@iba-team/portal-react-components/dist/svg/outline/trash.svg';
import { ReactComponent as EditIcon } from '@iba-team/portal-react-components/dist/svg/outline/edit.svg';
import { prepareDateToString } from '../../utils/dateHelper';
import { enUS } from 'date-fns/locale';
import './ticket/TicketPopUp.scss';
export const checkPastDate = (date: Date) => new Date() > new Date(date);

interface FullCalendarProps {
  date: Date,
  dataSource: ReadonlyArray<SpecialDay>,
  setDataSource: (data: SpecialDay[])=> void,
}

interface PopUp {
  selectedDays: ReadonlyArray<SelectedDays>,
  day: Date,
  setDataSource: (data: SpecialDay[])=> void,
  dataSource: ReadonlyArray<SpecialDay>,
}

export const RenderPopUp: FunctionComponent<PopUp> = (props) => {
  const {t} = useTranslation();
  const isTablet = useDeviceSizeThreshold(BREAKPOINT_LG);
  const {isAllowedAccess} = useCheckAccess('CALENDAR_ADMIN');
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState<SelectedDays>({
    from: new Date(),
    to: new Date(),
    title: '',
    description: '',
    type: 'HOLIDAY',
    id: 0,
  });

  useEffect(() => {
    props.selectedDays.forEach(day => {
      const isBetween = DateUtils.isDayInRange(props.day, { from: day.from, to: day.to });
      if (isBetween) {
        setCurrent(day);
      }
    });
  }, [props.day, props.selectedDays]);

  const isWorking = (current.type === 'WORKING');
  const prepareStartDate = prepareDateToString(enUS)(current.from);
  const prepareEndDate = prepareDateToString(enUS)(current.to);
  const isOneDayEvt = isEqual(current.from, new Date(current.to));
  const getTodayClassName = () => {
    if(isToday(current.from)){
      return (isWorking ? 'special_day_working_today' : 'special_day_holiday_today');
    }
    return (isWorking ? 'special_day_working' : 'special_day_holiday');
  };
  // <TicketPopUp dataSource={props.dataSource} setDataSource={props.setDataSource} event={current} setOpen={() => console.log(20)} startKey={'from'} endKey={'to'} />;
  return (
    <Popover visible={visible} overlayClassName={'popupWrapper'} onOpenChange={() => setVisible(!visible)} className={'calendar_popover'} trigger={'click'} title={  <div className={'calendar_popover__title'}>
      <div className={'calendar_popover__title__left'}>
        {current.type === 'WORKING' ?
          <WorkingIcon/> :
          <StarIcon/>
        }
        <span>
          {current.title}
        </span>
      </div>
      <Tooltip title={t('close')}>
        <CloseIcon onClick={() => setVisible(false)} className={'close-icon'}/>
      </Tooltip>
    </div>} content={
      <div className={'calendar_popover__content'}>
        <div className={'calendar_popover__content__fields'}>
          <div className={'calendar_popover__content__fields__label'}>
            <span>
              {prepareStartDate('EEEE')}
            </span>
            {!isOneDayEvt &&
              <span style={{ paddingLeft: '5px' }}>
              - {prepareEndDate('EEEE')}
              </span>
            }
          </div>
          :
          <div className={'calendar_popover__content__fields__value'}>
            <span>{prepareStartDate('MMM')}</span>
            <span>{prepareStartDate('dd')}</span>
            {!isOneDayEvt &&
              <span>
                  - {prepareEndDate('dd')}
              </span>
            }
            ,
            <span> {prepareStartDate('yyyy')} </span>
          </div>
        </div>
        <div className={'calendar_popover__content__fields'}>
          <div className={'calendar_popover__content__fields__label'}>
            <span>{t('type')}:&nbsp;</span>
            {!isOneDayEvt &&
              <span style={{ paddingLeft: '5px' }}>
              - {prepareEndDate('EEEE')}
              </span>
            }
          </div>
          <div className={'calendar_popover__content__fields__value'}>
            <span>{t(current.type === 'HOLIDAY'
              ?
              'nonWorkingDay' : 'workingDay')}</span>
          </div>
        </div>
        {current.description &&
          <div className={'calendar_popover__content__fields'}>
            <div className={'calendar_popover__content__fields__label'}>
              <span>
                {t('modalFields_descr')}:&nbsp;
              </span>
            </div>
            <div  className={'calendar_popover__content__fields__value'}>
              <span>
                {current.description}
              </span>
            </div>
          </div>
        }
        {!isTablet && isAllowedAccess &&
          <>
            {!checkPastDate(current.from) &&
              <>
                <Divider/>
                <div className={'calendar_popover__content__footer'}>
                  <div className={'calendar_popover__content__footer__edit'}
                    onClick={() => {
                      editEventModalCall.showImmediately({
                        specialDays: {
                          dayType: current.type,
                          description: current.description,
                          endDate: current.from,
                          startDate: current.from,
                          title: current.title,
                          id: current.id,
                          isWeekend: current.isWeekend,
                        },
                        setDataSource: props.setDataSource,
                        dataSource: props.dataSource
                      }
                      );
                      setVisible(false);
                    }}>
                    <EditIcon/>
                    <span>{ t('edit') }</span>
                  </div>
                  <div className={'calendar_popover__content__footer__delete'}
                    onClick={() => {
                      deleteEventModalCall.showImmediately({specialDays: {
                        id: current.id, title: current.title, dayType: current.type
                      }, setDataSource: props.setDataSource, dataSource: props.dataSource });
                      setVisible(false);
                    }}>
                    <DeleteIcon/>
                    <span>{ t('delete') }</span>
                  </div>
                </div>
              </>
            }
          </>
        }
      </div>
    }>
      <div>
        <Tooltip title={!isWorking ? '' : t('workingDay')}>
          <div className={getTodayClassName()}>{format(props.day, 'd')}
            {isWorking && <div className={'working_circle'}/>}
          </div>
        </Tooltip>
      </div>
    </Popover>
  );
};

export const FullCalendar = observer((props: FullCalendarProps) => {
  const [selectedDays, setSelectedDays] = useState<SelectedDays[]>([]);
  const { t } = useTranslation();
  const weekdaysShort = t('weekdaysShort').split(',');
  const {isAllowedAccess} = useCheckAccess('CALENDAR_ADMIN');
  const isTablet = useDeviceSizeThreshold(BREAKPOINT_LG);

  useEffect(() => {
    setSelectedDays(props.dataSource.map(i => {
      return ({
        from: new Date(`${i.startDate + 'T12:00:00.000'}`),
        to: new Date(`${i.endDate + 'T12:00:00.000'}`),
        title: i.title,
        description: i.description,
        type: i.dayType,
        id: i.id,
        isWeekend: i.isWeekend,
      });
    }));
  }, [props.dataSource]);

  const range = eachDayOfInterval({start: startOfYear(new Date(getYear(props.date))), end:endOfYear(new Date(props.date))});
  const weekendDays = range.filter(i => isWeekend(i) && selectedDays.findIndex((value)=> format(value.from, 'yyyy-MM-dd') === format(i,'yyyy-MM-dd')) === -1);

  return (
    <DayPicker numberOfMonths={12} initialMonth={new Date(getYear(props.date), 0)}
      toMonth={new Date(getYear(props.date), 1)}
      month={new Date(getYear(props.date), 0)}
      selectedDays={selectedDays}
      className={'dayPicker'}
      firstDayOfWeek={1}
      showOutsideDays
      modifiers={{weekend: weekendDays}}
      navbarElement={() => null}
      renderDay={(day, modifiers) => (modifiers.selected) ?
        <RenderPopUp dataSource={props.dataSource} setDataSource={props.setDataSource} selectedDays={selectedDays} day={day} />
        :
        <div className={'day'}
          onClick={() =>  !checkPastDate(day) && isAllowedAccess && !isTablet &&
                        chooseEventTypeModalCall.showImmediately({
                          specialDays: {
                            startDate: day
                          },
                          setDataSource: props.setDataSource,
                          dataSource:  props.dataSource,
                        })}
        >
          {format(new Date(day), 'd')}
        </div>}
      captionElement={({ date }) => (
        <div className={'custom_month'}>
          <h3 className={'custom_month__name'}>{format(new Date(date), 'MMMM')}</h3>
          <div className={'custom_month__days'}>(
            {
              getDaysInMonth(date) - eachWeekendOfMonth(date).length -
              props.dataSource.filter(i =>
                isSameMonth(new Date(i.startDate), date) &&
                !isWeekend(new Date(i.startDate)) &&
                i.dayType !== 'WORKING').length
              + props.dataSource.filter(i => isSameMonth(new Date(i.startDate), date) && i.dayType === 'WORKING' && isWeekend(new Date(i.startDate))).length
            } working days)
          </div>
        </div>
      )}
      weekdaysShort={weekdaysShort}
    />

  );
});
